<script>
    import { createEventDispatcher, onMount } from "svelte";
    import { fetchGroupClients } from "../../../../js/credentialsApi.js";
    import PlayerManagement from "./PlayerManagement.svelte";
    import ProgressBanner from "./ProgressBanner.svelte";
    import { genBonusIdV2 } from "../../../utils/freebetsIgp.js";
    import backend from "../../../../server.js";
    import Select from 'svelte-select';
  import GamesManagementIgp from "./GamesManagementIgp.svelte";

    //  ================= Const =================
    const LIST_BRANDS  = ({ provider, page = 1, xpage = 200 } = {}) => backend.brands.list({ provider, page, xpage });
    const LIST_BRANDS_BY_CLIENT = ({ category = "", provider = "", clientCode = ""} = {}) => backend.games.listBrandsByClient({category, provider, clientCode});
    const LIST_CLIENTS = ({ page = 1, xpage = 200, search = "", ...rest } = {}) => backend.client.lists({ page, xpage, search, ...rest });
    const BETS_CACHE = new Map();
    const BETS_INFLIGHT = new Map();
    const makeKey = (clientCode, gameId, currency) => `${clientCode}|${gameId}|${currency}`;

    //  ================= Props =================
    export let isNew = false;
    export let manageClients = 'auto';  
    export let campaign;
    export let campaignType = "";
    export let selectionMode = "none";
    export let selectedClient = "";
    export let allClients = [];
    export let integration = {
        provider: "",
        maxDays: 14,
        validateClient: async () => ({}),
        getBetScales: null,
        demo: null,
        bonusPrefix: "",
        idStrategy: ""
    };
    export let saving = false;
    export let enabledTypes = { slot: true, live: true}
    export let creationFlow = null; // { stage: 'creating'|'adding'|'done'|'error', total, added, errors } | null
    export let brandOptions = [];
    export let brandsLoading = false;
    export let brandsError = '';
    let demoMode = false;
    let currencySel = '';
    let codeClientSelect = '';
    let category = "";

    //  ================= Const and helpers =================
    $: computedIdStrategy = integration?.idStrategy ?? 'auto';
    const dispatch = createEventDispatcher();
    $: providerName = integration.providerName;
    $: provider = integration.provider;
    $: demoConfig = integration.demo;
    $: betScalesLoader = integration.getBetScales;
    $: _maxDays = Number.isFinite(integration.maxDays)? +integration.maxDays : 14; // Fallback to 14 days if invalid
    $: DEMO_CURR = demoMode ? demoCurrency(demoConfig) : "";
    $: demoMode = !!demoConfig && (selectedClientCode === demoConfig.CODE || String(selectedClient) === String(demoConfig.ID));
    $: clientItems = Array.isArray(visibleClients)
        ? visibleClients.map(c => ({ value: String(c.id), label: `${c.code} — ${c.namesite}` }))
        : [];
    $: typesEnabled = { slot: !!enabledTypes?.slot, live: !!enabledTypes?.live };
    $: typeItems = [
        ...(typesEnabled.slot ? [{ value: 'SLOT', label: 'Slots (freespins)' }] : []),
        ...(typesEnabled.live ? [{ value: 'LIVE', label: 'Live (bono)' }] : []),
    ];

    let _lastSelectedClient = null;
    $: if (String(selectedClient ?? '') !== String(_lastSelectedClient ?? '')) {
        _lastSelectedClient = selectedClient == null ? null : String(selectedClient);
        queueMicrotask(() => {
            try {
                _clientchange();
                const applied = applyCurrencyFromMapIfAny(selectedClientObj);
                if (applied) {
                    currencySel = String(campaign.currency || '').toUpperCase();
                }
            } catch (err) {
                console.error('Watcher de selectedClient fallo:', err);
            }
        });
    }

    //  ================= Local variables =================
    let showManagePlayers = false;
    let selectedPlayers = []; // [{id, label}]
    let showManageGames = false;
    $: selectedPlayersLabel = selectedPlayers.length
        ? (selectedPlayers.length === 1 ? selectedPlayers[0].label : `${selectedPlayers.length} jugadores seleccionados`)
        : "";
    let betScales = [];
    let betScalesLoading = false;
    let betScalesError = '';
    let lastBetScalesKey = '';
    let betScalesTried = false;
    let lastProvider = null;
    let formErrors = {};
    let submittedOnce = false;
    let isLoadingClients = false;
    let selectedGamesLabel = "";
    let selectedGameLabels = [];
    let campaignName = "";

    //  ================= Reactive =================
    $: selectedClientId = (typeof selectedClient === 'object' && selectedClient)
        ? String(selectedClient.value ?? selectedClient.id ?? '')
        : String(selectedClient || '');
    $: selectedClientObj = allClients.find(c => String(c.id) === selectedClientId);
    $: selectedClientCode = selectedClientObj?.code ?? "";
    
    // Demo Mode
    $: demoGameLocked = demoMode && demoHasDefaultsFor(demoConfig, campaignType);
    $: gamesText = campaign.slotConfigs?.length
    ? campaign.slotConfigs.map(cfg => cfg.game.gameName).join(', ')
    : 'Ningun juego seleccionado';
    $: if (
        demoMode && demoConfig && campaignType &&
        demoHasDefaultsFor(demoConfig, campaignType) &&
        !campaign.gameIdentifier && (!Array.isArray(campaign.gameIdentifiers) || campaign.gameIdentifiers.length === 0)
    ) {
        applyDemoDefaultsFor(campaignType, demoConfig);
        queueMicrotask(fetchBetScalesIfNeeded);
    }
    $: busyNow = !!creationFlow || saving;
    // Clean if not client
    $: if (!selectedClient) {
        selectedPlayers = [];
        campaign.currency = "";
        currencySel = "";
        clearGameSelection();
    }
    // Bet scales
    $: if (isNew && campaignType === 'SLOT' && selectedClientCode && campaign.gameIdentifier && campaign.currency) {
        void fetchBetScalesIfNeeded();
    }
    // Validation
    $: if (submittedOnce) { formErrors = validateGeneric(buildGenericPayload(), { maxDays: _maxDays }); }
    $: if (isNew) { formErrors = {}; submittedOnce = false; }
    $: if (provider && provider !== lastProvider) {
        lastProvider = provider;
        if (!Array.isArray(brandOptions) || !brandOptions.length) {
            brandOptions = [];
            if (campaignType) fetchBrandsFromAPI();
        }
        BETS_CACHE.clear();
        BETS_INFLIGHT.clear();
    }
    $: canPickPlayers = !!selectedClient && !!campaign.currency && !saving;
    $: needGameAndCurrency = !campaign.gameIdentifier || !campaign.currency;

    // ================= Bet scales loader =================
    async function fetchBetScalesIfNeeded() {
        if (!betScalesLoader || campaignType !== "SLOT") return;
        const clientCode = selectedClientCode || '';
        const gameId = campaign.gameIdentifier || '';
        const currency = getCurrencyCode(campaign.currency);
        if (!clientCode || !gameId || !currency) return;
        const key = makeKey(clientCode, gameId, currency);
        if (key === lastBetScalesKey && (betScales.length || betScalesLoading)) return;
        // cache hit
        const cached = BETS_CACHE.get(key);
        if (Array.isArray(cached) && cached.length) {
            lastBetScalesKey = key;
            betScales = cached;
            betScalesTried = true;
            ensureAmountIn(cached);
            return;
        }
        // inflight
        const inflight = BETS_INFLIGHT.get(key);
        if (inflight) {
            betScalesLoading = true;
            betScalesError = '';
            try {
                const list = await inflight;
                lastBetScalesKey = key;
                betScales = list;
                betScalesTried = true;
                ensureAmountIn(list);
            } catch (err) {
                console.error('Error cargando bet scales (inflight):', err);
                betScales = [];
                betScalesError = 'No se pudieron cargar montos disponibles.';
            } finally {
                betScalesLoading = false;
            }
            return;
        }
        // new request
        betScalesLoading = true;
        betScalesError = '';
        betScalesTried = true;
        const p = (async () => {
            const raw = await betScalesLoader({ clientCode, gameId, currency });
            return Array.isArray(raw) ? raw : [];
        })();
        BETS_INFLIGHT.set(key, p);
        try {
            const list = await p;
            BETS_CACHE.set(key, list);
            lastBetScalesKey = key;
            betScales = list;
            ensureAmountIn(list);
        } catch (err) {
            console.error('Error cargando bet scales:', err);
            betScales = [];
            betScalesError = 'No se pudieron cargar montos disponibles.';
        } finally {
            BETS_INFLIGHT.delete(key);
            betScalesLoading = false;
        }
    }

    //  ================= Backend call's =================
    async function fetchBrandsFromAPI() {
        if (!provider) { brandOptions = []; return; }
        const clientIdRaw = selectedClientId || '';
        if (!clientIdRaw) { brandOptions = []; return; }
        const clientId = Number(clientIdRaw);
        if (!clientId || Number.isNaN(clientId)) {
            console.warn("fetchBrandsFromAPI: clientId inválido:", clientIdRaw);
            brandOptions = [];
            return;
        }
        if (brandsLoading) return;
        brandOptions = [];
        brandsLoading = true;
        brandsError = '';

        try {
            const res = await LIST_BRANDS_BY_CLIENT({ category: campaignType == "SLOT" ? "slot" : campaignType == "LIVE" ? "slot_live" : undefined, provider, clientCode: codeClientSelect });
            const rows = Array.isArray(res?.data) ? res.data : (Array.isArray(res?.data?.list) ? res.data.list : []);
            const map = new Map(
                rows.filter(r => r).map(raw => {
                    const s = String(raw);
                    return [s, { value: s, label: s.replace(/_/g, " ") }];
                })
            );
            brandOptions = Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, "es", { sensitivity: "base" }));
        } catch (err) {
            console.error("Error al obtener marcas por cliente:", err);
            brandsError = 'No se pudieron cargar las marcas para este cliente.';
            brandOptions = [];
        } finally {
            brandsLoading = false;
        }
    }
    function isProdClient(c){
        const v = c?.isProd ?? c?.is_prod ?? c?.isprod;
        return v === 1 || v === true || v === "1";
    }
    async function fetchClientsFromAPI() {
        try {
            isLoadingClients = true;
            await loadSessionCodes();
            const params = { page: 1, xpage: 200, search: "", summary: true };
            if (Array.isArray(expandedSessionCodes) && expandedSessionCodes.length > 0) {
                params.codes = expandedSessionCodes.join(',');
            }
            const res = await LIST_CLIENTS(params);
            const rows = Array.isArray(res?.data?.list) ? res.data.list : [];
            const onlyProd = rows;//.filter(isProdClient);
            allClients = onlyProd.sort((a, b) => (a.code || "").localeCompare(b.code || ""));
        } catch (err) {
            console.error("Error al obtener clientes:", err);
            allClients = [];
        } finally {
            isLoadingClients = false;
        }
    }

    //  ================= Helpers payloads and validations =================
    function getCurrencyCode(v) {
        if (v && typeof v === 'object') {
            return String(v.value ?? v.code ?? v.currency ?? v.label ?? '').toUpperCase();
        }
        return String(v ?? '').toUpperCase();
    }
    function ensureAmountIn(list) { if (campaign.amount && !list.includes(Number(campaign.amount))) campaign.amount = ''; }
    function resetBetScalesState() {
        betScales = [];
        betScalesError = '';
        lastBetScalesKey = '';
        betScalesTried = false;
        BETS_INFLIGHT.clear();
    }
    function clearGameSelection() {
        // if(campaign.gameIdentifier || campaign.gameIdentifiers?.length) return;
        campaign.brand = "";
        campaign.gameIdentifier = "";
        campaign.gameIdentifiers = [];
        selectedGamesLabel = "";
        selectedGameLabels = [];
        campaign.amount = "";
        resetBetScalesState();
    }

    function genCampaignId({ clientCode = '', type = '', currency = '', provider = '' }) {
        const c = (clientCode || '').replace(/[^A-Za-z0-9]/g,'').slice(0,4).toUpperCase();
        const t = (type || '')[0]?.toUpperCase() || 'X';
        const cu = (currency || '')[0]?.toUpperCase() || 'X';
        const p = (provider || '')[0]?.toUpperCase() || 'X';
        const rand = Math.random().toString(36).slice(2,6).toUpperCase();
        const time = Date.now().toString(36).slice(-2).toUpperCase();
        return `${c}${t}${cu}${p}${rand}${time}`.slice(0,15);
    }
    function inferStatus(start, end) {
        const now = new Date();
        const s = start ? new Date(start) : null;
        const e = end ? new Date(end) : null;
        if (!s || !e) return 4; // SCHEDULED
        if (now < s) return 4;  // SCHEDULED
        if (now > e) return 3;  // EXPIRED
        return 1;               // ACTIVE
    }
    function clearErrorV2(keys) {
        const arr = Array.isArray(keys) ? keys : [keys];
        for (const k of arr) delete formErrors[k];
        formErrors = { ...formErrors }; 
    }
    function clearError(keys) {
        queueMicrotask(fetchBrandsFromAPI);
        const arr = Array.isArray(keys) ? keys : [keys];
        for (const k of arr) delete formErrors[k];
        formErrors = { ...formErrors }; 
    }
    function daysBetween(a, b) {
        return (b - a) / (1000 * 60 * 60 * 24);
    }
    function applyDemoDefaultsForType() {
        if (!demoConfig || !demoGameLocked) return;
        if (campaignType === 'SLOT') {
            campaign.gameIdentifier = demoConfig.SLOT_GAME?.id || "";
            campaign.gameIdentifiers = [];
            selectedGamesLabel = demoConfig.SLOT_GAME?.name || "";
            selectedGameLabels = [demoConfig.SLOT_GAME?.name || ""];
            if (selectedClientCode && campaign.currency) queueMicrotask(fetchBetScalesIfNeeded);
        } else if (campaignType === 'LIVE') {
            campaign.gameIdentifier = "";
            campaign.gameIdentifiers = (demoConfig.LIVE_GAMES || []).map(g => g.id);
            selectedGamesLabel = (demoConfig.LIVE_GAMES || []).map(g => g.name).join(', ');
            selectedGameLabels = (demoConfig.LIVE_GAMES || []).map(g => g.name);
        }
    }
    function buildGenericPayload() {
        const liveLabels = selectedGameLabels.length
            ? selectedGameLabels
            : (selectedGamesLabel || "").split(",").map(s => s.trim()).filter(Boolean);
            const base = {
            campaignName: (campaignName || "").trim() || null,
            campaignType: campaignType || "", // "SLOT" | "LIVE"
            currency: campaign.currency || "",
            client: selectedClientObj ? {
                clientId: selectedClientObj.id ?? null,
                clientCode: selectedClientObj.code || "",
                clientName: selectedClientObj.namesite || ""
            } : null,
            provider,           // p.ej. "adm"
            startDate: campaign.startTime ? new Date(campaign.startTime).toISOString() : null,
            endDate:   campaign.endTime   ? new Date(campaign.endTime).toISOString()   : null,
            // SLOT
            slotConfigs: (campaignType === "SLOT")
            ? (campaign.slotConfigs || []).map(cfg => ({
                game: {
                  gameId: String(cfg.game.gameId),
                  gameName: cfg.game.gameName,
                  brand: cfg.game.brand.value
                },
                amountPerRound: Number(cfg.amountPerRound || 0),
                rounds: Number(cfg.rounds || 0)
              }))
            : null,
        };
        return Object.fromEntries( Object.entries(base).filter(([, v]) => v !== undefined) );
    }
    function payloadPlayerList() {       
        return {
            players: selectedPlayers.map(p => ({
                playerId: String(p.username || p.id),
                baseBonusCode: genBonusIdV2(integration?.bonusPrefix || "") || null
            }))
        };
    }
    function validateGeneric(p, { maxDays = 14 } = {}) {
        const errors = {};
        // Commons
        if (!selectedClient) errors.clientId = "Selecciona un cliente";
        if (!p.campaignName) errors.campaignName = "Nombre de campaña requerido";
        if (!p.campaignType) errors.type = "Selecciona un tipo";
        // currency is always set from client
        if (!p.startDate) errors.startTime = "Fecha/hora de inicio requerida";
        if (!p.endDate) errors.endTime = "Fecha/hora de fin requerida";
        if (p.startDate && p.endDate) {
            const start = new Date(p.startDate);
            const end = new Date(p.endDate);
            if (!(end > start)) {
                errors.endTime = "La fecha de fin debe ser posterior al inicio";
            } else if (daysBetween(start, end) > maxDays) {
                errors.endTime = `El rango no puede superar ${maxDays} días`;
            }
        }
        return errors;
    }
    // ================= Handlers / Dispatch =================
    const _addToEnd = (detail) => dispatch("addtoend", detail);
    function bumpEnd(delta) {
        _addToEnd(delta);
        clearError("endTime");
    }
    async function validateSelectedClient() {
        try {
            const res = await integration.validateClient(selectedClientCode);
            alert("✅ Cliente válido:\n" + JSON.stringify(res?.data ?? res, null, 2));
        } catch (err) {
            alert(`❌ Credenciales no encontradas`);
        }
    }
    const _close = () => {
        formErrors = {};
        submittedOnce = false;
        dispatch("close");
    };
    const _save = () => {
        if (saving) return;
        const payload = buildGenericPayload();
        const payloadPlayers = payloadPlayerList();
        const errs = validateGeneric(payload, { maxDays: _maxDays });
        formErrors = errs;
        submittedOnce = true;
        if (Object.keys(errs).length) { return; }
        console.log("PLAYER LIST2:", payload);
        console.log("PLAYER payload:", payloadPlayers);
        dispatch("submit", {payload, players: payloadPlayers});
        dispatch("save");
    };
    function _clientchange() {
        try {
            console.debug('[_clientchange] selectedClient=', selectedClient);
            clearError(["clientId","playerId"]);
            const client = (allClients || []).find(c => String(c.id) === String(selectedClient));
            codeClientSelect = client?.code ?? "";
            const willBeDemo = !!demoConfig && (codeClientSelect === demoConfig.CODE || String(selectedClient) === String(demoConfig.ID));
            selectedPlayers = [];
            if (willBeDemo) {
                const def = (demoCurrency(demoConfig) || "FUN").toUpperCase();
                if (def) { campaign.currency = def; currencySel = def; }
                if (campaignType && demoHasDefaultsFor(demoConfig, campaignType)) {
                    applyDemoDefaultsFor(campaignType, demoConfig);
                    queueMicrotask(fetchBetScalesIfNeeded);
                }
            } else {
                selectedPlayers = [];
                clearGameSelection();
            }
            dispatch("clientchange", { id: selectedClient, codeClientSelect, client });
            brandOptions = [];
            brandsError = '';
            if (campaignType) queueMicrotask(fetchBrandsFromAPI);
        } catch (err) {
            console.error('_clientchange caught error:', err);
        }
    }
    let _lastType = null;
    $: if (campaignType !== _lastType) {
        let value = campaignType || '';
        const allowed =
            value === '' ||
            (value === 'SLOT' && typesEnabled.slot) ||
            (value === 'LIVE' && typesEnabled.live);
        if (!allowed) {
            _lastType = '';
            campaignType = '';
            clearGameSelection();
            clearError(["type","gameIdentifier","gameIdentifiers","amount","rounds","initialBalance","maxWin"]);
            dispatch("typechange", { value: '' });
        } else {
            clearError(["type","gameIdentifier","gameIdentifiers","amount","rounds","initialBalance","maxWin"]);
            if (value === "SLOT") {
                campaign.initialBalance = "";
                campaign.maxWin = "";
            } else if (value === "LIVE") {
                campaign.amount = "";
                campaign.rounds = "";
            }
            clearGameSelection();
            dispatch("typechange", { value });
            if (value && selectedClient) {
                queueMicrotask(fetchBrandsFromAPI);
            }
            if (demoMode && demoHasDefaultsFor(demoConfig, value)) {
                queueMicrotask(() => {
                    applyDemoDefaultsFor(value, demoConfig);
                    queueMicrotask(fetchBetScalesIfNeeded);
                });
            }
            _lastType = value;
        }
    }
    const _openManage = () =>
        demoGameLocked
            ? alert('Modo demo: los juegos se asignan automáticamente según el tipo.')
            : (showManageGames = true);

    const _confirmGames = (e) => {
        clearError(["gameIdentifiers"]);
        const { brand, games, labels } = e.detail || {};
        selectedGameLabels = Array.isArray(labels) ? labels : [];
        campaign.brand = brand || "";
        delete campaign.gameIdentifier;
        resetBetScalesState();
        queueMicrotask(fetchBetScalesIfNeeded);
        showManageGames = false;
        dispatch("confirmgames", e.detail);
    };
    function demoPlayers(cfg){ return Array.isArray(cfg?.PLAYERS) ? cfg.PLAYERS : []; }
    function demoPrimary(cfg){ const p = demoPlayers(cfg)[0]; return p ? { id:String(p.id), label:p.label||String(p.id), currency:String(p.currency||cfg?.DEFAULTS?.CURRENCY||'') } : null; }
    function demoCurrency(cfg){ return cfg?.DEFAULTS?.CURRENCY || demoPrimary(cfg)?.currency || ''; }
    function demoHasDefaultsFor(cfg, type){
        if (!cfg) return false;
        if (type === 'SLOT') return !!cfg.SLOT_GAME?.id;
        if (type === 'LIVE') return Array.isArray(cfg.LIVE_GAMES) && cfg.LIVE_GAMES.length > 0;
        return false;
    }
    function applyDemoDefaultsFor(type, cfg){
        if (!cfg) return;
        if (type === 'SLOT'){
            campaign.gameIdentifier = cfg.SLOT_GAME?.id || "";
            campaign.gameIdentifiers = [];
            selectedGameLabels = [cfg.SLOT_GAME?.name || ""];
            selectedGamesLabel  = selectedGameLabels[0] || "";
            if (selectedClientCode && campaign.currency) queueMicrotask(fetchBetScalesIfNeeded);
        } else if (type === 'LIVE'){
            const arr = Array.isArray(cfg.LIVE_GAMES) ? cfg.LIVE_GAMES : [];
            campaign.gameIdentifier = "";
            campaign.gameIdentifiers = arr.map(g => g.id);
            selectedGameLabels = arr.map(g => g.name);
            selectedGamesLabel = selectedGameLabels.join(', ');
        }
    }
    let _sessionClientTried = false;
    let sessionClientLocked = false;
    function getSessionClientInfo() {
        try {
            const raw = sessionStorage.getItem('user') || '{}';
            const data = JSON.parse(raw);
            const c_id = data?.c_id ?? data?.clientId ?? data?.cId ?? null;
            const client_code = data?.client_code ?? data?.clientCode ?? null;
            return { c_id, client_code };
        } catch (err) {
            return { c_id: null, client_code: null };
        }
    }
    async function trySelectClientFromSession() {
        if (_sessionClientTried) return;
        _sessionClientTried = true;
        const { c_id, client_code } = getSessionClientInfo();
        if (c_id == null && !client_code) return;
        if (!Array.isArray(allClients) || allClients.length === 0) return;
        if (client_code) {
            const sc = String(client_code).trim();
            const group = await fetchGroupClients(sc);
            if (Array.isArray(group) && group.length > 1) return;
            const byCode = allClients.find(c => String(c.code) === sc);
            if (byCode) {
                selectedClient = String(byCode.id);
                sessionClientLocked = true;
                queueMicrotask(() => { _clientchange(); applyCurrencyFromMapIfAny(byCode); });
                return;
            }
        }
        const byId = allClients.find(c => String(c.id) === String(c_id));
        if (byId) {
            selectedClient = String(byId.id);
            sessionClientLocked = true;
            queueMicrotask(() => { _clientchange(); applyCurrencyFromMapIfAny(byId); });
        }
    }
    $: if (!_sessionClientTried && Array.isArray(allClients) && allClients.length > 0) trySelectClientFromSession();

    // Multiple Clients
    let expandedSessionCodes = [];
    async function loadSessionCodes() {
        try {
            const raw = sessionStorage.getItem('user') || '{}';
            const data = JSON.parse(raw);
            const singleCode = data?.client_code ?? data?.clientCode ?? null;
            if (singleCode) {
                const sc = String(singleCode).trim();
                const codes = await fetchGroupClients(sc);
                if (Array.isArray(codes) && codes.length) {
                    expandedSessionCodes = Array.from(new Set(codes.map(String)));
                    return;
                }
            }
        } catch {}
        expandedSessionCodes = [];
    }
    function getSessionClientCodesExpanded() {
        try {
            const raw = sessionStorage.getItem('user') || '{}';
            const data = JSON.parse(raw);
            const singleCode = data?.client_code ?? data?.clientCode ?? null;
            if (singleCode) {
                const sc = String(singleCode).trim();
                return [sc];
            }
            const singleId = data?.c_id ?? data?.clientId ?? null;
            if (singleId != null) { return []; }
            return [];
        } catch (err) {
            return [];
        }
    }
    // New state
    let sessionClientList = null;
    let visibleClients = [];
    $: {
        const sessionCodes = expandedSessionCodes.length ? expandedSessionCodes : getSessionClientCodesExpanded();
        let sessionIds = [];
        try {
            const raw = sessionStorage.getItem('user') || '{}';
            const data = JSON.parse(raw);
            if (Array.isArray(data?.clients) && data.clients.length) {
                sessionIds = data.clients.map(c => c.id).filter(Boolean).map(String);
            } else {
                const singleId = data?.c_id ?? data?.clientId ?? null;
                if (singleId != null) sessionIds = [String(singleId)];
            }
        } catch (err) { sessionIds = []; }
        if ((!Array.isArray(allClients) || allClients.length === 0)) {
            visibleClients = [];
            sessionClientList = null;
        } else {
            if ((sessionCodes && sessionCodes.length) || (sessionIds && sessionIds.length)) {
                const allowedCodes = new Set((sessionCodes||[]).map(String));
                const allowedIds = new Set((sessionIds||[]).map(String));
                const filtered = allClients.filter(c => allowedCodes.has(String(c.code)) || allowedIds.has(String(c.id)));
                visibleClients = filtered;   
                sessionClientList = filtered.map(c => String(c.id));
            } else {
                visibleClients = allClients;
                sessionClientList = null;
            }
        }
        if (visibleClients.length === 1) {
            const only = visibleClients[0];
            if (!selectedClient) {
                selectedClient = String(only.id);
                sessionClientLocked = true;
                queueMicrotask(() => {
                    _clientchange();
                    applyCurrencyFromMapIfAny(only);
                });
            }
        }
    }

    function applyCurrencyFromMapIfAny(client) {
        if (!client) return false;
        const mapped = client.currency || "FUN";
        campaign.currency = mapped;
        currencySel = String(mapped).toUpperCase();
        return true;
    }

    onMount(() => {
        loadSessionCodes();
        const wantSelf = manageClients !== 'parent';
        const empty = !Array.isArray(allClients) || allClients.length === 0;
        if (wantSelf && empty) fetchClientsFromAPI();
        else {
            if (Array.isArray(allClients) && allClients.length > 0) {
                trySelectClientFromSession();
            }
        }
        currencySel = String(campaign?.currency || '').toUpperCase();
    });
</script>

{#if isNew}
    <div class="modal-backdrop fade show"></div>
    <div class="modal d-block tight-2col" tabindex="-1" style="background:rgba(0,0,0,.3);">
        <div class="modal-dialog modal-lg modal-dialog-centered">
            <div class="modal-content">
                <!-- HEADER -->
                <div class="modal-header">
                    <h5 class="modal-title">Crear Campaña {String(providerName || "")}</h5>
                    <button type="button" class="btn-close" on:click={_close} disabled={saving}></button>
                </div>

                <!-- BODY -->
                <div class="modal-body">
                    {#if creationFlow}
                        {#key `${creationFlow.stage}:${creationFlow.total}`}
                            <ProgressBanner
                                    stage={creationFlow.stage}
                                    total={creationFlow.total}
                                    done={creationFlow.added}
                                    errors={creationFlow.errors}
                                    sticky={true}
                            />
                        {/key}
                    {:else if saving}
                        <ProgressBanner stage="saving" total={0} sticky={true} />
                    {/if}

                        <!-- General Details -->
                    <h6 class="section-title">Detalles generales</h6>
                    <div class="section-box mb-3">
                        <div class="player-row">
                            <!-- Client -->
                            <div class="client-col">
                                <label for="clients" class="form-label">Cliente</label>
                                <div id="clients" class="d-flex gap-2 align-items-start">
                                    <!--<div class="w-75 field-col">-->
                                    <div class="w-100 field-col">
                                        <!--<Select
                                                items={clientItems}
                                                bind:value={selectedClient}
                                                reduce={(item) => item?.value}
                                                placeholder={isLoadingClients ? 'Cargando clientes…' : 'Selecciona un cliente'}
                                                disabled={isLoadingClients || saving || sessionClientLocked}
                                                inputId="clients"
                                                clearable={false}
                                                class="{formErrors.clientId ? 'is-invalid' : ''}"
                                        />-->
                                        <select
                                                id="selectedClient"
                                                class="form-control"
                                                bind:value={selectedClient}
                                                disabled={isLoadingClients || saving || sessionClientLocked}
                                                on:change={() => _clientchange()}
                                        >
                                            <option value="" disabled>Selecciona cliente</option>
                                            {#each visibleClients as c}
                                                <option value={String(c.id)}>{c.code} — {c.namesite}</option>
                                            {/each}
                                        </select>

                                        <div id="clientIdFeedback" class="feedback-slot">
                                            {#if formErrors.clientId}
                                                <small class="text-danger">{formErrors.clientId}</small>
                                            {/if}
                                            {#if demoMode}
                                                <small class="badge bg-danger-subtle text-danger border border-danger rounded-pill mt-1">
                                                    Modo Demo
                                                </small>
                                            {/if}
                                        </div>
                                    </div>
                                    <!--<button type="button" class="btn btn-outline-info w-25"
                                            on:click={validateSelectedClient} disabled={saving || !selectedClient}>
                                        Validar
                                    </button>-->
                                </div>
                            </div>

                            <!-- Campaign Name -->
                            <div>
                                <label for="campaignName" class="form-label">Nombre de Campaña </label>
                                <input
                                        id="campaignName"
                                        class="form-control"
                                        bind:value={campaignName}
                                        placeholder="p.ej. Navidad2026"
                                />
                            </div>

                            <!-- Currency (read-only, set from client) -->
                            <div>
                                <label for="currencySelect" class="form-label">Moneda</label>
                                <input
                                        id="currencySelect"
                                        class="form-control"
                                        value={currencySel}
                                        disabled
                                />
                            </div>

                            <!-- Player Data -->
                            <div class="player-col">
                                <label for="playersRO" class="form-label">Jugadores <span class="text-muted">(opcional)</span></label>
                                <div class="input-group">
                                    <input
                                            id="playersRO"
                                            class="form-control readonly-style {formErrors.playerId ? 'is-invalid' : ''}"
                                            value={selectedPlayersLabel || 'Ningún jugador seleccionado'}
                                            readonly
                                            aria-invalid={formErrors.playerId ? 'true' : 'false'}
                                    />
                                    <button
                                            type="button"
                                            class="btn btn-outline-secondary"
                                            on:click={() => (showManagePlayers = true)}
                                            disabled={!canPickPlayers}
                                    >
                                        Seleccionar…
                                    </button>
                                </div>
                                {#if formErrors.playerId}
                                    <div class="invalid-feedback d-block mt-1">{formErrors.playerId}</div>
                                {/if}
                            </div>
                        </div>
                    </div>

                    <!-- Game Details -->
                    <h6 class="section-title">Detalles del juego</h6>
                    <div class="section-box mb-3">
                        <div class="modal-grid">
                            <!-- Type -->
                            <div>
                                <label for="campaignType" class="form-label">Tipo</label>
                                <select
                                        id="campaignType"
                                        class="form-control {formErrors.type ? 'is-invalid' : ''}"
                                        bind:value={campaignType}
                                        disabled={saving}
                                        on:change={() => clearError('type')}
                                >
                                    <option value="" disabled selected={campaignType === ""}>
                                        Selecciona un tipo
                                    </option>
                                    {#if typesEnabled.slot}
                                        <option value="SLOT">Slots (freespins)</option>
                                    {/if}
                                    <!-- {#if typesEnabled.live}
                                        <option value="LIVE">Live (bono)</option>
                                    {/if} -->
                                </select>
                                {#if formErrors.type}
                                    <small class="text-danger">{formErrors.type}</small>
                                {/if}
                            </div>

                            <!-- Games -->
                            <div>
                                <label for="gamesRO" class="form-label">Juego(s)</label>
                                <div>
                                    <div class="input-group">
                                        {#key `ro:${campaignType}:${campaign.brand}:${selectedGamesLabel}`}
                                        <input
                                                    id="gamesRO"
                                                    class="form-control readonly-style {(formErrors.gameIdentifier || formErrors.gameIdentifiers) ? 'is-invalid' : ''}"
                                                    value={gamesText}
                                                    readonly
                                                    aria-invalid={(formErrors.gameIdentifier || formErrors.gameIdentifiers) ? 'true' : 'false'}
                                                    aria-describedby="gamesFeedback"
                                            />
                                        {/key}
                                        <button type="button" class="btn btn-outline-secondary" on:click={_openManage} disabled={!campaignType || demoGameLocked || saving || brandsLoading || !brandOptions.length}>
                                            Seleccionar…
                                        </button>
                                        {#if brandsLoading}
                                            <div class="form-text">Cargando marcas para el cliente seleccionado…</div>
                                        {:else if brandsError}
                                            <div class="form-text text-danger">{brandsError}</div>
                                        {/if}

                                    </div>

                                    {#if formErrors.gameIdentifier}
                                        <div id="gamesFeedback" class="invalid-feedback d-block mt-1">
                                            {formErrors.gameIdentifier}
                                        </div>
                                    {:else if formErrors.gameIdentifiers}
                                        <div id="gamesFeedback" class="invalid-feedback d-block mt-1">
                                            {formErrors.gameIdentifiers}
                                        </div>
                                    {/if}
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Bonus details -->
                    <h6 class="section-title">Detalles del bono</h6>
                    <div class="section-box mb-3">
                        <div class="modal-grid">
                            <div>
                                Juegos añadidos: {gamesText}
                            </div>
                            <div>
                                <label for="startTime" class="form-label">Inicio</label>
                                <input id="startTime" type="datetime-local"
                                       class="form-control {formErrors.startTime ? 'is-invalid' : ''}"
                                       bind:value={campaign.startTime}
                                       on:change={() => clearError("startTime")}
                                />
                                {#if formErrors.startTime}<small class="text-danger">{formErrors.startTime}</small>{/if}
                            </div>
                            <!-- End Time -->
                            <div>
                                <label for="endTime" class="form-label">Fin</label>
                                <input id="endTime" type="datetime-local"
                                       class="form-control {formErrors.endTime ? 'is-invalid' : ''}"
                                       bind:value={campaign.endTime}
                                       on:change={() => clearError("endTime")}
                                />
                                {#if formErrors.endTime}<small class="text-danger">{formErrors.endTime}</small>{/if}
                                <div class="d-flex gap-2 mt-2">
                                    <button type="button" class="btn btn-outline-secondary btn-sm" on:click={() => bumpEnd({ hours: 1 })}>+1hora</button>
                                    <button type="button" class="btn btn-outline-secondary btn-sm" on:click={() => bumpEnd({ days: 1 })}>+1día</button>
                                    <button type="button" class="btn btn-outline-secondary btn-sm" on:click={() => bumpEnd({ weeks: 1 })}>+7días</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- FOOTER -->
                <div class="modal-footer">
                    <button class="btn btn-secondary" on:click={_close} disabled={busyNow}>Cancelar</button>
                    <button class="btn btn-primary" on:click={_save} disabled={busyNow}>
                        Guardar
                    </button>
                </div>
            </div>
        </div>
    </div>

    {#if showManageGames}
        <div class="nested-dim" aria-hidden="true"></div>
        <!-- {#key `gm:${campaignType}:${selectionMode}`} -->
            <GamesManagementIgp
                    {codeClientSelect}
                    {brandOptions}
                    {provider}
                    {campaignType}
                    {campaign}
                    {betScales}
                    {betScalesTried}
                    {betScalesLoader}
                    {betScalesLoading}
                    {betScalesError}
                    {formErrors}
                    {needGameAndCurrency}
                    initialBrand={campaign.brand || ""}
                    initialSelected={
                      Array.isArray(campaign.slotConfigs)
                        ? campaign.slotConfigs.map(cfg => String(cfg.game.gameId))
                        : []
                    }
                    {selectionMode}
                    on:close={() => (showManageGames = false)}
                    on:confirm={_confirmGames}
                    on:addgameconfig={(e) => {
                            const game = e.detail;
                           campaign.slotConfigs = [
                            ...(campaign.slotConfigs || []),
                            {
                                game: {
                                    gameId: game.gameId,
                                    gameName: game.gameName,
                                    brand: game.brand
                                },
                                amountPerRound: game.amountPerRound,
                                rounds: game.rounds
                            }
                        ]; 
                    }}
                    on:removegameconfig={(e) => {
                        const { gameId } = e.detail;                 
                        campaign.slotConfigs = campaign.slotConfigs.filter(
                            (cfg) => cfg.game.gameId !== gameId
                        );
                    }}
                    on:selectgame={(e) => {
                        campaign.gameIdentifier = e.detail.gameId;
                        fetchBetScalesIfNeeded();
                    }}
            />
        <!-- {/key} -->
    {/if}
    {#if showManagePlayers && campaign.currency}
        <div class="nested-dim" aria-hidden="true"></div>
        <PlayerManagement
                clientId={typeof selectedClient === 'object' ? String(selectedClient?.value ?? selectedClient?.id ?? '') : String(selectedClient || '')}
                currency={getCurrencyCode(campaign?.currency)}
                initialSelected={selectedPlayers.map(p => String(p.id))}
                staticPlayers={
                    demoMode && demoPlayers(demoConfig).length
                    ? demoPlayers(demoConfig).map(p => ({
                        value: String(p.id),
                        label: p.label || String(p.id),
                        currency: String(p.currency || demoCurrency(demoConfig) || "")
                    }))
                  : null
                }
                idStrategy={computedIdStrategy}
                on:close={() => (showManagePlayers = false)}
                on:confirm={(e) => {
                    const items = e.detail?.players || [];
                    selectedPlayers = items.map(p => ({ id: String(p.id), label: p.label || String(p.id) }));
                    showManagePlayers = false;
                    clearError("playerId");
            }}
        />
    {/if}
{/if}

<style>
    .modal-grid { display:grid; grid-template-columns: repeat(2, 1fr); gap:1rem; padding:1rem 0; }
    .modal-grid > div { display:flex; flex-direction:column; }
    .modal-content { padding: 1.5rem; font-size: 1rem; position: relative; }
    .section-box { border: 1px solid var(--bs-border-color, #e5e7eb); border-radius: 12px; padding: 16px; background: #fff; }
    .section-title { margin: 0 0 8px 0; font-weight: 650; font-size: .95rem; }
    .readonly-style { background-color:#f5f5f5; color:#666; }
    .nested-dim { position: fixed; inset:0; z-index:1055; background: rgba(0,0,0,.28); pointer-events:none; }
    select option { color:#111 }
    select option[value=""], select option[disabled]{ color:#666 }
    .player-row { display:grid; grid-template-columns: 1fr 1fr; gap:1rem; padding:1rem 0; }
    .client-col { display:flex; flex-direction:column; }
    .player-col { display:flex; flex-direction:column; }
    .feedback-slot { min-height: 1.25rem; }

    .tight-2col .modal-content { padding: 1rem; }
    .tight-2col .section-box { padding: 12px; }
    .tight-2col .form-label { margin-bottom: .25rem; }
    .tight-2col .modal-dialog { --bs-modal-width: 920px; }

    .tight-2col{ --field-max: 540px;}
    .tight-2col .form-control,
    .tight-2col .input-group { max-width: var(--field-max); }
    .tight-2col #endTime + .d-flex {
        max-width: var(--field-max);
    }
</style>
